import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import logger, { nativeConsole } from '../utils/logger';
import { mapErrorToUserMessage } from '../utils/userErrorMessage';
import { getPeriodError } from '../utils/billingPeriodValidation';
import { hasSellerTaxIdentification } from '../utils/sellerProfile';

// Mobile Components
import MobileInvoiceDashboard from '../components/invoice/mobile/MobileInvoiceDashboard';
import { useDeviceDetection } from '../hooks/useDeviceDetection';

// Component Imports
import InvoiceMetaForm from '../components/invoice/InvoiceMetaForm';
import BuyerDetailsForm from '../components/invoice/BuyerDetailsForm';
import InvoiceItemsForm from '../components/invoice/InvoiceItemsForm';
import SellerDetailsForm from '../components/invoice/SellerDetailsForm';
import ShipToAddressForm from '../components/invoice/ShipToAddressForm';
import OptionalFieldsForm from '../components/invoice/OptionalFieldsForm';
import RelatedOfferSelector from '../components/invoice/RelatedOfferSelector';
import CollapsibleSection from '../components/ui/CollapsibleSection';
import { ConfirmationDialog } from '../components/ConfirmationDialog';
import { Button } from '../components/common/Button';
import InvoiceTypeSwitch from '../components/ui/InvoiceTypeSwitch';
import { InvoiceNumberPreview } from '../components/invoice/InvoiceNumberPreview';
import InvoiceDraftSelector from '../components/invoice/InvoiceDraftSelector';
import DocumentLanguageSelector from '../components/invoice/DocumentLanguageSelector';
import TranslationReviewModal from '../components/translation/TranslationReviewModal';
import useTranslationReview from '../hooks/useTranslationReview';
import { InvoiceUsageMeter } from '../components/usage/InvoiceUsageMeter';
import { useUsageLimitValueFraming } from '../hooks/invoice/useUsageLimitValueFraming';
import { FREEMIUM_INVOICE_LIMIT } from '../config/freemium';
import { INVOICE_FORM_ROUTE } from '../config/cockpitAccess';
import { buildUpgradeHref } from '../config/nudgePromotions';
import analyticsService from '../services/analyticsService';

// Types
import { Seller, Buyer, ShipToAddress, InvoiceItem, InvoiceData } from '../types/invoiceTypes';
import { DownPayment } from '../types/downPaymentTypes';

// Down Payment Components
import DownPaymentModal from '../components/modals/DownPaymentModal';

// Hooks
import { useInvoiceForm } from '../hooks/invoice/useInvoiceForm';
import { useInvoiceSubmission } from '../hooks/invoice/useInvoiceSubmission';
import { getTaxCategoryEntry, extractParagraphNr, requiresZeroRateCategoryChoice } from '../utils/taxCategoryCatalog';
import { useCustomerManagement } from '../hooks/invoice/useCustomerManagement';
import { useProductManagement } from '../hooks/invoice/useProductManagement';
import { useInvoiceNumberPreview } from '../hooks/invoice/useInvoiceNumberPreview';

// Firebase
import { auth, db } from '../firebaseConfig';
import { signOut } from 'firebase/auth';

// Draft API Functions
import { 
    saveInvoiceDraft, 
    generateZugferdPreview, 
    listInvoiceDrafts, 
    getInvoiceDraftDetails, 
    deleteInvoiceDraft 
} from '../api/invoiceDraftApi';
import { getAuth } from 'firebase/auth';
import { SingleValue } from 'react-select';
import { formatCurrency, formatDate as formatDateCentral } from '../utils/formatters';
import { centsToEuros } from '../utils/moneyUtils';

// VB-1 Feature-Flag (build-time, fail-closed, Default AUS).
const TAX_CATEGORY_ENABLED = process.env.REACT_APP_TAX_CATEGORY_ENABLED === 'true';

const InvoiceDashboard: React.FC = () => {
    const { t, i18n } = useTranslation(['invoice', 'common']);
    const [hasRequiredSellerData, setHasRequiredSellerData] = useState<boolean>(true);
    const navigate = useNavigate();
    const { isMobile } = useDeviceDetection();
    const [searchParams, setSearchParams] = useSearchParams();

    // Draft Management State
    const [availableDrafts, setAvailableDrafts] = useState<any[]>([]);
    const [selectedDraftId, setSelectedDraftId] = useState<string | null>(null);
    const [currentDraftId, setCurrentDraftId] = useState<string | null>(null); // Aktuelle Entwurf-ID für Updates
    
    // ⭐ NEU: Stornorechnungs-State
    const [cancellationForInvoiceId, setCancellationForInvoiceId] = useState<string | null>(null);
    const [cancellationInvoiceNumber, setCancellationInvoiceNumber] = useState<string | null>(null);
    // NEU: Email-Versand an Kunden
    const [sendToEmail, setSendToEmail] = useState<string | null>(null);
    const isLoadingCancellationRef = useRef(false); // Ref um Mehrfachladen zu verhindern
    const errorRef = useRef<HTMLDivElement>(null); // Ref für Auto-Scroll zum Fehler
    const [isLoadingDrafts, setIsLoadingDrafts] = useState(false);
    const [isLoadingDraftDetails, setIsLoadingDraftDetails] = useState(false);
    const [draftListError, setDraftListError] = useState<string | null>(null);
    const [isSavingDraft, setIsSavingDraft] = useState(false);
    const [isGeneratingPreview, setIsGeneratingPreview] = useState(false);
    const [draftSaveSuccess, setDraftSaveSuccess] = useState<string | null>(null); // Erfolgs-Nachricht für Entwurf-Speicherung
    const [token, setToken] = useState<string | null>(null);

    // Down Payment Modal State
    const [showDownPaymentModal, setShowDownPaymentModal] = useState<boolean>(false);

    // Auth Context
    const { currentUser, sellerData, sellerDataLoading, saveSellerData, refreshUserData, isPremium } = useAuth();
    
    // Custom Hooks
    const { 
        customerMeta, 
        checkForUpdate: checkCustomerUpdate, 
        handleCustomerSelection,
        dialogOpen: customerDialogOpen,
        setDialogOpen: setCustomerDialogOpen,
        dialogConfig: customerDialogConfig,
        handleDialogAction: handleCustomerDialogAction,
        releaseCustomerMetaLock,
        getCurrentCustomerMeta
    } = useCustomerManagement(); 

    const {
        productMetaForItems,
        checkAndHandleProductUpdate,
        handleProductSelection: handleProductSelectionForHook,
        resetProductManagement,
        dialogOpen: productDialogOpen,
        setDialogOpen: setProductDialogOpen,
        dialogConfig: productDialogConfig,
        handleDialogAction: handleProductDialogAction,
        getProductMetaForItems
    } = useProductManagement();

    const { 
        // Form state values
        isAdvancedMode, invoiceDate, currency, buyer, items, deliveryDate, 
        useDifferentShipTo, shipToAddress, paymentTerms, notes, billingStartDate, 
        billingEndDate, tenderOrLotReference, invoicedObjectId, invoicedObjectScheme, 
        contractReference, projectReferenceId, projectReferenceName, paymentDueDate, 
        paymentMandateReference, buyerOrderReference, sellerOrderReference, selectedOfferId, 
        selectedOfferNumber, relatedOfferData, offerWorkflowReferenceId,
        // Down payment state values
        existingDownPayments, hasDownPayments, totalDownPaymentAmount, remainingAmount, 
        isLoadingDownPayments, showDownPaymentSection,
        // Inline down payment creation state values
        inlineDownPaymentType, inlineDownPaymentValue, inlineDownPaymentDate, 
        inlineDownPaymentNotes, isCreatingInlineDownPayment,
        // NEU: Mehrsprachigkeit
        documentLanguage,
        // VB-1: Steuerart-Default
        defaultTaxCategorySelection,
        // Form state setters
        setIsAdvancedMode, setInvoiceDate, setCurrency, setBuyer, setItems, setDeliveryDate,
        setUseDifferentShipTo, setShipToAddress, setPaymentTerms, setNotes, setBillingStartDate,
        setBillingEndDate, setTenderOrLotReference, setInvoicedObjectId, setInvoicedObjectScheme,
        setContractReference, setProjectReferenceId, setProjectReferenceName, setPaymentDueDate,
        setPaymentMandateReference, setBuyerOrderReference, setSellerOrderReference, setSelectedOfferId,
        setSelectedOfferNumber, setRelatedOfferData, setOfferWorkflowReferenceId,
        // Down payment setters
        setExistingDownPayments, setHasDownPayments, setTotalDownPaymentAmount, setRemainingAmount,
        setIsLoadingDownPayments, setShowDownPaymentSection,
        // Inline down payment creation setters
        setInlineDownPaymentType, setInlineDownPaymentValue, setInlineDownPaymentDate,
        setInlineDownPaymentNotes, setIsCreatingInlineDownPayment,
        // NEU: Mehrsprachigkeit
        setDocumentLanguage,
        // VB-1: Steuerart-Default
        setDefaultTaxCategorySelection,
        resetForm: resetInvoiceForm,
        handleOfferSelection,
        handleInlineDownPaymentCreation,
        offerSelectionError,
        loadCancellationInvoiceData
    } = useInvoiceForm();

    // Re-Loop-Auflage 🟡-2 (review-report-leistungszeitraum-mobil.md): derselbe
    // Fehlerzustand wie in OptionalFieldsForm, hier gebraucht, um ihn zusätzlich
    // am (auch zugeklappten) Abschnittskopf sichtbar zu machen.
    const billingPeriodError = useMemo(
        () => getPeriodError(billingStartDate, billingEndDate),
        [billingStartDate, billingEndDate]
    );

    // Invoice number is now generated server-side, no client-side logic needed

    const {
        submitInvoice,
        isLoading: isSubmitting,
        error: submissionError,
        errorType: submissionErrorType,
        setError: setSubmissionError
    } = useInvoiceSubmission('zugferd');

    const {
        isModalOpen: isTranslationModalOpen,
        isLoading: isTranslationLoading,
        fields: translationFields,
        handleFieldChange: handleTranslationFieldChange,
        prepareAndOpenModal: prepareTranslationModal,
        handleConfirm: confirmTranslation,
        handleCancel: cancelTranslation,
    } = useTranslationReview();

    // Rechnungsnummer-Vorschau Hook
    const {
        zugferdPreview,
        isLoading: isLoadingPreview
    } = useInvoiceNumberPreview();

    // Effects
    useEffect(() => {
        if (!sellerDataLoading) {
            const basicData = !!(sellerData && sellerData.name && sellerData.street && sellerData.zip && sellerData.city && sellerData.country);
            
            // § 14 Abs. 4 Nr. 2 UStG: Steuernummer oder USt-IdNr. für ALLE Verkäufer (#339)
            const taxDataOk = hasSellerTaxIdentification(sellerData);
            
            setHasRequiredSellerData(basicData && taxDataOk);
        }
    }, [sellerData, sellerDataLoading]);

    useEffect(() => {
        const hasShipToData = !!(shipToAddress.name || shipToAddress.street || shipToAddress.street2 || shipToAddress.street3 || (shipToAddress.zip && shipToAddress.zip !== '') || (shipToAddress.city && shipToAddress.city !== '') || (shipToAddress.country && shipToAddress.country !== 'DE'));
        if (hasShipToData !== useDifferentShipTo) {
            setUseDifferentShipTo(hasShipToData);
        }
    }, [shipToAddress, useDifferentShipTo, setUseDifferentShipTo]);

    // Token Management
    useEffect(() => {
        const getToken = async () => {
            if (currentUser) {
                try {
                    const authToken = await getAuth().currentUser?.getIdToken(true);
                    setToken(authToken || null);
                } catch (error) {
                    console.error("Fehler beim Laden des Auth-Tokens:", error);
                    setToken(null);
                }
            } else {
                setToken(null);
            }
        };
        getToken();
    }, [currentUser]);

    // ⭐ NEU: URL-Parameter für Stornorechnungen verarbeiten
    useEffect(() => {
        const cancellationInvoiceId = searchParams.get('cancellationFor');
        const cancellationInvoiceNum = searchParams.get('invoiceNumber');
        
        // Nur verarbeiten wenn Parameter vorhanden sind UND wir nicht bereits laden
        if (cancellationInvoiceId && cancellationInvoiceNum && loadCancellationInvoiceData && !isLoadingCancellationRef.current) {
            logger.info(`URL-Parameter für Stornorechnung erkannt: ${cancellationInvoiceId}`);
            
            // Markiere dass wir laden um Mehrfachaufrufe zu verhindern
            isLoadingCancellationRef.current = true;
            
            // Setze State-Variablen für die Stornierung
            setCancellationForInvoiceId(cancellationInvoiceId);
            setCancellationInvoiceNumber(cancellationInvoiceNum);
            
            // Lade die Rechnungsdaten für die Stornorechnung
            loadCancellationInvoiceData(cancellationInvoiceId, cancellationInvoiceNum)
                .then(() => {
                    logger.info('Stornorechnung erfolgreich geladen');
                    // URL-Parameter entfernen, damit sie nicht erneut verarbeitet werden
                    setSearchParams({});
                })
                .catch((error) => {
                    logger.error('Fehler beim Laden der Stornorechnung:', error);
                    alert(t('cancellation.errors.loadFailed', 'Fehler beim Laden der Stornorechnungsdaten: ') + error.message);
                    // Bei Fehler State zurücksetzen
                    setCancellationForInvoiceId(null);
                    setCancellationInvoiceNumber(null);
                    // Reset loading flag bei Fehler
                    isLoadingCancellationRef.current = false;
                });
        }
    }, [searchParams, loadCancellationInvoiceData, setSearchParams, t]);

    // ⭐ NEU: URL-Parameter für Angebots-Vorauswahl verarbeiten
    useEffect(() => {
        const offerIdParam = searchParams.get('offerId');
        
        // Nur verarbeiten wenn Parameter vorhanden ist und noch kein Angebot ausgewählt wurde
        if (offerIdParam && !selectedOfferId && currentUser) {
            logger.info(`URL-Parameter für Angebots-Vorauswahl erkannt: ${offerIdParam}`);
            
            // Lade das Angebot aus Firestore und rufe handleOfferSelection auf
            const loadAndSelectOffer = async () => {
                try {
                    const { doc, getDoc } = await import('firebase/firestore');
                    const offerRef = doc(db, 'offers', offerIdParam);
                    const offerSnap = await getDoc(offerRef);
                    
                    if (offerSnap.exists()) {
                        const offerData = offerSnap.data();
                        const offerNumber = offerData.offerNumber || '';
                        
                        logger.info('Angebot aus Firestore geladen:', { offerNumber, offerId: offerIdParam });
                        
                        // Rufe handleOfferSelection mit vollständigen Daten auf
                        handleOfferSelection(offerIdParam, offerNumber, offerData);
                        
                        // URL-Parameter entfernen nach Verarbeitung
                        const newParams = new URLSearchParams(searchParams);
                        newParams.delete('offerId');
                        setSearchParams(newParams, { replace: true });
                        
                        logger.info('Angebot vorausgewählt und URL-Parameter entfernt');
                    } else {
                        logger.warn('Angebot nicht in Firestore gefunden:', offerIdParam);
                        // Trotzdem URL-Parameter entfernen
                        const newParams = new URLSearchParams(searchParams);
                        newParams.delete('offerId');
                        setSearchParams(newParams, { replace: true });
                    }
                } catch (error) {
                    logger.error('Fehler beim Laden des Angebots:', error);
                    // Trotzdem URL-Parameter entfernen
                    const newParams = new URLSearchParams(searchParams);
                    newParams.delete('offerId');
                    setSearchParams(newParams, { replace: true });
                }
            };
            
            loadAndSelectOffer();
        }
    }, [searchParams, selectedOfferId, currentUser, handleOfferSelection, setSearchParams]);

    // Load Drafts
    useEffect(() => {
        const loadDrafts = async () => {
            if (!token) return;
            
            setIsLoadingDrafts(true);
            setDraftListError(null);
            
            try {
                const drafts = await listInvoiceDrafts(token);
                // Filter only ZUGFeRD drafts for this dashboard
                const zugferdDrafts = drafts.filter(draft => draft.type === 'zugferd');
                setAvailableDrafts(zugferdDrafts);
                console.log('ZUGFeRD-Draft-Liste geladen:', zugferdDrafts);
            } catch (error: any) {
                console.error('Fehler beim Laden der ZUGFeRD-Drafts:', error);
                setDraftListError(mapErrorToUserMessage(error, t, { fallbackKey: 'errors.draft.loadFailed' }));
            } finally {
                setIsLoadingDrafts(false);
            }
        };

        loadDrafts();
    }, [token, t]);

    // Event-Listener für Draft-Löschung nach Rechnungserstellung
    useEffect(() => {
        const handleDraftDeleted = (event: CustomEvent) => {
            const { deletedDraftId } = event.detail;
            
            // Entferne den gelöschten Draft aus der lokalen Liste
            setAvailableDrafts(prevDrafts => 
                prevDrafts.filter(draft => draft.id !== deletedDraftId)
            );
            
            // Resetiere die Auswahl, falls der gelöschte Draft ausgewählt war
            if (selectedDraftId === deletedDraftId) {
                setSelectedDraftId(null);
            }
            
            logger.info(`Draft-Liste aktualisiert nach Löschung von Draft: ${deletedDraftId}`);
        };

        // Event-Listener hinzufügen
        window.addEventListener('draftDeleted', handleDraftDeleted as EventListener);

        // Cleanup beim Unmount
        return () => {
            window.removeEventListener('draftDeleted', handleDraftDeleted as EventListener);
        };
    }, [selectedDraftId]);

    // Callback Handlers
    const handleLogout = useCallback(async () => {
        try {
            await signOut(auth);
            navigate('/login');
        } catch (error) {
            logger.error(error, "logout error");
        }
    }, [navigate]);

    const resetAllForms = useCallback(() => {
        resetInvoiceForm();
        resetProductManagement();
        setCurrentDraftId(null); // Reset der aktuellen Draft-ID bei Formular-Reset
        setSelectedDraftId(null); // Reset auch der ausgewählten Draft-ID
        // ⭐ NEU: Reset der Stornorechnungs-Felder
        setCancellationForInvoiceId(null);
        setCancellationInvoiceNumber(null);
        isLoadingCancellationRef.current = false; // Reset loading flag
        setSendToEmail(null);
        nativeConsole.log("All relevant form states reset.");
    }, [resetInvoiceForm, resetProductManagement]);

    const closeAllDialogs = useCallback(() => {
        setCustomerDialogOpen(false);
        setProductDialogOpen(false);
    }, [setCustomerDialogOpen, setProductDialogOpen]);

    // Handle form submit
    const handleFormSubmit = useCallback(async (e: React.FormEvent) => {
        e.preventDefault();
        
        nativeConsole.log('=== handleFormSubmit START ===');
        nativeConsole.log('Form submitted');

        try {
            // VB-1: Fail-closed-Validierung der Steuerart (jurist A1/A2/VETO-4). Nie still durchlassen.
            if (TAX_CATEGORY_ENABLED) {
                // 0 %-Satz ist mehrdeutig: jede 0 %-Position MUSS eine eindeutige
                // Steuerart tragen, sonst entstünde eine widersprüchliche Rechnung
                // (0 % + Kategorie „Standard"). Submit hart blockieren und auf die
                // betroffenen Positionen zeigen.
                const unresolvedZeroRatePositions = items
                    .map((item, index) => ({ item, index }))
                    .filter(({ item }) => requiresZeroRateCategoryChoice(item.taxRate, item.taxCategorySelection))
                    .map(({ index }) => index + 1);
                if (unresolvedZeroRatePositions.length > 0) {
                    setSubmissionError(
                        t('taxCategory.validation.zeroRateCategoryRequired', {
                            positions: unresolvedZeroRatePositions.join(', '),
                        })
                    );
                    return;
                }
                for (const item of items) {
                    const entry = getTaxCategoryEntry(item.taxCategorySelection);
                    if (!entry) continue;
                    if (entry.requiresBuyerVatId && !(buyer.vatNumber ?? '').trim()) {
                        setSubmissionError(t('taxCategory.validation.buyerVatIdRequired'));
                        return;
                    }
                    if (entry.requiresParagraphNr && !extractParagraphNr(item.taxCategorySelection, item.exemptionReason).trim()) {
                        setSubmissionError(t('taxCategory.validation.paragraphNrRequired'));
                        return;
                    }
                    // VETO-4: Kategorie K (§ 6a IGL) erfordert Leistungs-/Lieferdatum (BT-72, EN-16931).
                    if (entry.bt151Code === 'K' && !(deliveryDate ?? '').trim()) {
                        setSubmissionError(t('taxCategory.validation.kDeliveryDateRequired'));
                        return;
                    }
                }
            }

            // BT-72-Guard für Stornorechnungen (Fix 2026-08-07, Folge-Ticket aus
            // Review-Report #327).
            //
            // `loadCancellationInvoiceData` befüllt das Leistungsdatum der Storno aus
            // der Originalrechnung, mit Fallback deliveryDate → invoiceDate → null
            // (useInvoiceForm.ts). Fehlen im Original BEIDE Daten, bleibt das Feld leer
            // — und zwar STILL: bis hierher gab es keinen Hinweis, die Storno wäre ohne
            // Leistungszeitpunkt (BT-72) rausgegangen.
            //
            // Das ist bei einem Storno gravierender als bei einer normalen Rechnung: Er
            // korrigiert eine bereits erbrachte Leistung, deren Zeitpunkt nach
            // § 14 Abs. 4 Nr. 6 UStG zu den Pflichtangaben gehört, und ist der Beleg,
            // mit dem die ursprüngliche Steuerschuld nach § 17 UStG berichtigt wird.
            // Ohne Datum ist die Zuordnung zum Voranmeldungszeitraum nicht belegt.
            //
            // Bewusst ein harter Stopp und keine bloße Warnung — dieselbe Härte wie beim
            // Kategorie-K-Guard oben: Das Feld ist im Formular editierbar, der Nutzer
            // kann das Datum der Originalleistung eintragen und erneut absenden.
            // Greift ausschließlich bei Stornos; normale Rechnungen bleiben unberührt.
            if (cancellationForInvoiceId && !(deliveryDate ?? '').trim()) {
                setSubmissionError(
                    t(
                        'cancellation.errors.missingDeliveryDate',
                        'Für diese Stornorechnung konnte kein Leistungs-/Lieferdatum aus der Originalrechnung übernommen werden (BT-72). Der Leistungszeitpunkt ist eine Pflichtangabe nach § 14 Abs. 4 Nr. 6 UStG. Bitte tragen Sie das Datum der ursprünglichen Leistung im Formular ein.',
                    ),
                );
                return;
            }

            // **FIX 34: Koordinierte Validierung für normale Rechnungseinreichung**
            nativeConsole.log('Calling validateAndHandleUpdates...');
            const validationSuccessful = await validateAndHandleUpdates();
            nativeConsole.log('validateAndHandleUpdates result:', validationSuccessful);
            
            if (!validationSuccessful) {
                nativeConsole.log('Validation failed - stopping submission');
                return; // Dialog ist offen oder Fehler aufgetreten
            }
            
            nativeConsole.log('Starting invoice submission...');
            // **FIX: Verwende getCurrentCustomerMeta und getProductMetaForItems für aktuellste Daten**
            const currentCustomerMeta = getCurrentCustomerMeta();
            const currentProductMeta = getProductMetaForItems();
            nativeConsole.log('CustomerMeta passed to submitInvoice:', currentCustomerMeta);
            nativeConsole.log('CustomerMeta action:', currentCustomerMeta.action);
            nativeConsole.log('CustomerMeta customerId:', currentCustomerMeta.customerId);
            nativeConsole.log('ProductMeta passed to submitInvoice:', currentProductMeta);

            const resolvedItems = items.map(item => ({
                ...item,
                itemAction: currentProductMeta[item.id]?.action || 'none',
                productId: currentProductMeta[item.id]?.productId || null,
            }));

            const doSubmit = async (translatedItems: typeof resolvedItems, translatedPaymentTerms: string | null | undefined, translatedNotes: string | null | undefined, translatedProjectName: string | null | undefined) => {
                await submitInvoice(
                    {
                        // invoiceNumber is now generated server-side
                        invoiceDate, currency, buyer,
                        deliveryDate, useDifferentShipTo, shipToAddress: useDifferentShipTo ? shipToAddress : null,
                        paymentTerms: translatedPaymentTerms ?? null, notes: translatedNotes ?? null, billingStartDate, billingEndDate, tenderOrLotReference,
                        invoicedObjectId, invoicedObjectScheme, contractReference, projectReferenceId,
                        projectReferenceName: translatedProjectName ?? null, paymentDueDate, paymentMandateReference, buyerOrderReference,
                        sellerOrderReference, selectedOfferId, offerWorkflowReferenceId,
                        customerMeta: currentCustomerMeta,
                        items: translatedItems,
                        productAction: Object.values(currentProductMeta).some(meta => meta.action === 'create' || meta.action === 'update')
                                    ? 'update'
                                    : 'none',
                        selectedDraftId,
                        cancellationForInvoiceId,
                        cancellationInvoiceNumber,
                        documentLanguage,
                        existingDownPayments: hasDownPayments ? existingDownPayments : undefined,
                        sendToEmail: sendToEmail || null,
                    },
                    resetAllForms
                );
                nativeConsole.log('Invoice submission completed successfully');
                releaseCustomerMetaLock();
            };

            const preferredLang = sellerData?.preferredLanguage || 'de';
            if (documentLanguage !== preferredLang) {
                // Open translation modal before submitting
                const formDataForTranslation = {
                    items: resolvedItems,
                    paymentTerms,
                    notes,
                    projectReferenceName,
                };
                await prepareTranslationModal(
                    formDataForTranslation,
                    documentLanguage,
                    false, // not an offer
                    async (translatedData) => {
                        await doSubmit(
                            translatedData.items,
                            translatedData.paymentTerms,
                            translatedData.notes,
                            translatedData.projectReferenceName
                        );
                    }
                );
            } else {
                await doSubmit(resolvedItems, paymentTerms, notes, projectReferenceName);
            }
        } catch (error) {
            nativeConsole.error('Fehler bei der Rechnungserstellung:', error);
            setSubmissionError(mapErrorToUserMessage(error, t, { fallbackKey: 'errors.invoice.saveFailed' }));
        }
    }, [
        buyer, 
        items,
        customerDialogOpen,
        productDialogOpen, 
        checkCustomerUpdate,
        checkAndHandleProductUpdate,
        submitInvoice,
        invoiceDate, 
        currency, 
        deliveryDate, 
        useDifferentShipTo, 
        shipToAddress,
        paymentTerms, 
        notes, 
        billingStartDate, 
        billingEndDate, 
        tenderOrLotReference,
        invoicedObjectId, 
        invoicedObjectScheme, 
        contractReference, 
        projectReferenceId,
        projectReferenceName, 
        paymentDueDate, 
        paymentMandateReference, 
        buyerOrderReference,
        sellerOrderReference, 
        selectedOfferId, 
        offerWorkflowReferenceId,
        customerMeta,
        productMetaForItems,
        resetAllForms,
        setSubmissionError,
        selectedDraftId,
        // ⭐ NEU: Stornorechnungs-Dependencies
        cancellationForInvoiceId,
        cancellationInvoiceNumber,
        // ⭐ NEU: Abschlagszahlungs-Dependencies
        hasDownPayments,
        existingDownPayments,
        documentLanguage,
        getCurrentCustomerMeta,
        releaseCustomerMetaLock,
        getProductMetaForItems,
        sellerData,
        prepareTranslationModal,
        sendToEmail,
    ]);

    // Customer Dialog Handler
    const onCustomerDialogAction = useCallback(async (action: string) => {
        try {
            const result = await handleCustomerDialogAction(action);
            logger.debug(`Kundendialog wurde mit Ergebnis geschlossen: ${result}`);
            
            if (result === 'continue') {
                const productResult = await checkAndHandleProductUpdate(items);
                if (productResult === false || productResult === 'continue') {
                    // **FIX: Verwende getProductMetaForItems für aktuellste Daten**
                    const currentProductMeta = getProductMetaForItems();
                    const currentCustomerMeta = getCurrentCustomerMeta();
                    const resolvedItems = items.map(item => ({
                        ...item,
                        itemAction: currentProductMeta[item.id]?.action || 'none',
                        productId: currentProductMeta[item.id]?.productId || null,
                    }));
                    const doSubmit = async (tItems: typeof resolvedItems, tPayment: string | null | undefined, tNotes: string | null | undefined, tProject: string | null | undefined) => {
                        await submitInvoice(
                            {
                                invoiceDate, currency, buyer,
                                deliveryDate, useDifferentShipTo, shipToAddress: useDifferentShipTo ? shipToAddress : null,
                                paymentTerms: tPayment ?? null, notes: tNotes ?? null, billingStartDate, billingEndDate, tenderOrLotReference,
                                invoicedObjectId, invoicedObjectScheme, contractReference, projectReferenceId,
                                projectReferenceName: tProject ?? null, paymentDueDate, paymentMandateReference, buyerOrderReference,
                                sellerOrderReference, selectedOfferId, offerWorkflowReferenceId,
                                customerMeta: currentCustomerMeta,
                                items: tItems,
                                productAction: Object.values(currentProductMeta).some(m => m.action === 'create' || m.action === 'update') ? 'update' : 'none',
                                selectedDraftId, cancellationForInvoiceId, cancellationInvoiceNumber, documentLanguage,
                                existingDownPayments: hasDownPayments ? existingDownPayments : undefined,
                            },
                            resetAllForms
                        );
                        releaseCustomerMetaLock();
                    };
                    const preferredLang = sellerData?.preferredLanguage || 'de';
                    if (documentLanguage !== preferredLang) {
                        await prepareTranslationModal(
                            { items: resolvedItems, paymentTerms, notes, projectReferenceName },
                            documentLanguage, false,
                            async (td) => doSubmit(td.items, td.paymentTerms, td.notes, td.projectReferenceName)
                        );
                    } else {
                        await doSubmit(resolvedItems, paymentTerms, notes, projectReferenceName);
                    }
                }
            }
        } catch (error) {
            setSubmissionError(`Fehler beim Kundendialog: ${(error as Error).message}`);
            nativeConsole.error("Fehler beim Kundendialog:", error);
        }
    }, [
        handleCustomerDialogAction, items, checkAndHandleProductUpdate, submitInvoice,
        invoiceDate, currency, buyer, deliveryDate, useDifferentShipTo, shipToAddress,
        paymentTerms, notes, billingStartDate, billingEndDate, tenderOrLotReference,
        invoicedObjectId, invoicedObjectScheme, contractReference, projectReferenceId,
        projectReferenceName, paymentDueDate, paymentMandateReference, buyerOrderReference,
        sellerOrderReference, selectedOfferId, offerWorkflowReferenceId, customerMeta,
        productMetaForItems, resetAllForms, setSubmissionError, selectedDraftId,
        cancellationForInvoiceId, cancellationInvoiceNumber, documentLanguage,
        hasDownPayments, existingDownPayments, getCurrentCustomerMeta, releaseCustomerMetaLock,
        getProductMetaForItems, sellerData, prepareTranslationModal,
    ]);

    // Product Dialog Handler
    const onProductDialogAction = useCallback(async (action: string) => {
        try {
            const result = await handleProductDialogAction(action);
            logger.debug(`Produktdialog wurde mit Ergebnis geschlossen: ${result}`);
            
            if (result === 'continue') {
                // **FIX: Verwende getProductMetaForItems für aktuellste Daten nach Dialog-Aktion**
                const currentProductMeta = getProductMetaForItems();
                nativeConsole.log('ProductMeta after dialog action:', currentProductMeta);
                const currentCustomerMeta = getCurrentCustomerMeta();
                const resolvedItems = items.map(item => ({
                    ...item,
                    itemAction: currentProductMeta[item.id]?.action || 'none',
                    productId: currentProductMeta[item.id]?.productId || null,
                }));
                const doSubmit = async (tItems: typeof resolvedItems, tPayment: string | null | undefined, tNotes: string | null | undefined, tProject: string | null | undefined) => {
                    await submitInvoice(
                        {
                            invoiceDate, currency, buyer,
                            deliveryDate, useDifferentShipTo, shipToAddress: useDifferentShipTo ? shipToAddress : null,
                            paymentTerms: tPayment ?? null, notes: tNotes ?? null, billingStartDate, billingEndDate, tenderOrLotReference,
                            invoicedObjectId, invoicedObjectScheme, contractReference, projectReferenceId,
                            projectReferenceName: tProject ?? null, paymentDueDate, paymentMandateReference, buyerOrderReference,
                            sellerOrderReference, selectedOfferId, offerWorkflowReferenceId,
                            customerMeta: currentCustomerMeta,
                            items: tItems,
                            productAction: Object.values(currentProductMeta).some(m => m.action === 'create' || m.action === 'update') ? 'update' : 'none',
                            selectedDraftId, cancellationForInvoiceId, cancellationInvoiceNumber, documentLanguage,
                            existingDownPayments: hasDownPayments ? existingDownPayments : undefined,
                        },
                        resetAllForms
                    );
                    releaseCustomerMetaLock();
                };
                const preferredLang = sellerData?.preferredLanguage || 'de';
                if (documentLanguage !== preferredLang) {
                    await prepareTranslationModal(
                        { items: resolvedItems, paymentTerms, notes, projectReferenceName },
                        documentLanguage, false,
                        async (td) => doSubmit(td.items, td.paymentTerms, td.notes, td.projectReferenceName)
                    );
                } else {
                    await doSubmit(resolvedItems, paymentTerms, notes, projectReferenceName);
                }
            }
        } catch (error) {
            setSubmissionError(`Fehler beim Produktdialog: ${(error as Error).message}`);
            nativeConsole.error("Fehler beim Produktdialog:", error);
        }
    }, [
        handleProductDialogAction, submitInvoice,
        invoiceDate, currency, buyer, items, deliveryDate, useDifferentShipTo, shipToAddress,
        paymentTerms, notes, billingStartDate, billingEndDate, tenderOrLotReference,
        invoicedObjectId, invoicedObjectScheme, contractReference, projectReferenceId,
        projectReferenceName, paymentDueDate, paymentMandateReference, buyerOrderReference,
        sellerOrderReference, selectedOfferId, offerWorkflowReferenceId, customerMeta,
        productMetaForItems, resetAllForms, setSubmissionError, selectedDraftId,
        cancellationForInvoiceId, cancellationInvoiceNumber, documentLanguage,
        hasDownPayments, existingDownPayments, getCurrentCustomerMeta, releaseCustomerMetaLock,
        getProductMetaForItems, sellerData, prepareTranslationModal,
    ]);

    // Draft Management Handlers
    const handleSaveDraft = async () => {
        if (!token) {
            console.error('Kein Auth-Token verfügbar für Draft-Speicherung');
            return;
        }

        setIsSavingDraft(true);

        try {
            const invoiceData = {
                invoiceDate, currency, buyer, items, deliveryDate, 
                useDifferentShipTo, 
                shipToAddress: useDifferentShipTo ? shipToAddress : null, 
                paymentTerms, notes, billingStartDate, billingEndDate, 
                tenderOrLotReference, invoicedObjectId, invoicedObjectScheme, 
                contractReference, projectReferenceId, projectReferenceName, 
                paymentDueDate, paymentMandateReference, buyerOrderReference,
                sellerOrderReference,
                seller: sellerData!,
                type: 'zugferd',
                // NEU: Angebots- und Workflow-Informationen für Draft speichern
                relatedOfferIds: selectedOfferId ? [selectedOfferId] : undefined,
                workflowReferenceId: offerWorkflowReferenceId || undefined,
                // NEU: Mehrsprachigkeit - Dokumentsprache
                documentLanguage, // ✅ Bereits vorhanden!
            };

            let draftId: string;

            // Prüfen ob bereits ein Entwurf existiert (currentDraftId oder selectedDraftId)
            const existingDraftId = currentDraftId || selectedDraftId;
            
            if (existingDraftId) {
                // Bestehenden Entwurf löschen, bevor neuer erstellt wird
                console.log('Lösche bestehenden ZUGFeRD-Draft vor Neuerstellung:', existingDraftId);
                try {
                    await deleteInvoiceDraft(existingDraftId, token);
                    console.log('Alter Draft erfolgreich gelöscht, erstelle jetzt neuen');
                } catch (deleteError) {
                    console.warn('Draft-Löschung fehlgeschlagen, erstelle trotzdem neuen Draft:', deleteError);
                }
            }
            
            // Neuen Entwurf erstellen
            console.log('Erstelle neuen ZUGFeRD-Draft');
            const result = await saveInvoiceDraft(invoiceData, token);
            draftId = result.draftId;
            setCurrentDraftId(draftId); // Neue Draft-ID für zukünftige Updates merken
            setSelectedDraftId(draftId); // Auch als ausgewählten Entwurf setzen für UI-Anzeige
            console.log('ZUGFeRD-Draft erfolgreich erstellt:', draftId);

            // Draft-Liste neu laden
            const drafts = await listInvoiceDrafts(token);
            const zugferdDrafts = drafts.filter(draft => draft.type === 'zugferd');
            setAvailableDrafts(zugferdDrafts);

            // Erfolgs-Nachricht anzeigen
            setDraftSaveSuccess('Entwurf erfolgreich gespeichert!');
            // Nach 3 Sekunden wieder ausblenden
            setTimeout(() => setDraftSaveSuccess(null), 3000);
        } catch (error: any) {
            console.error('Fehler beim Speichern des ZUGFeRD-Drafts:', error);
            setDraftListError(mapErrorToUserMessage(error, t, { fallbackKey: 'errors.draft.saveFailed' }));
        } finally {
            setIsSavingDraft(false);
        }
    };

    const handleGeneratePreview = async () => {
        if (!token) {
            console.error('Kein Auth-Token verfügbar für Preview-Generierung');
            return;
        }

        setIsGeneratingPreview(true);

        try {
            const invoiceData = {
                invoiceDate, currency, buyer, items, deliveryDate, 
                useDifferentShipTo, 
                shipToAddress: useDifferentShipTo ? shipToAddress : null, 
                paymentTerms, notes, billingStartDate, billingEndDate, 
                tenderOrLotReference, invoicedObjectId, invoicedObjectScheme, 
                contractReference, projectReferenceId, projectReferenceName, 
                paymentDueDate, paymentMandateReference, buyerOrderReference,
                sellerOrderReference,
                seller: sellerData!,
                type: 'zugferd',
                // NEU: Mehrsprachigkeit - Dokumentsprache für Vorschau
                documentLanguage,
            };

            let pdfBlob: Blob;
            let draftId: string;

            // Prüfen ob bereits ein Entwurf existiert (currentDraftId oder selectedDraftId)
            const existingDraftId = currentDraftId || selectedDraftId;
            
            if (existingDraftId) {
                // Bestehenden Entwurf löschen, bevor neue Vorschau generiert wird
                console.log('Lösche bestehenden Draft vor neuer Vorschau-Generierung:', existingDraftId);
                try {
                    await deleteInvoiceDraft(existingDraftId, token);
                    console.log('Alter Draft erfolgreich gelöscht, generiere jetzt neue Vorschau');
                } catch (deleteError) {
                    console.warn('Draft-Löschung fehlgeschlagen, fahre mit normaler Vorschau fort:', deleteError);
                }
            }
            
            // Neue Vorschau generieren (erstellt neuen Entwurf)
            console.log('Generiere neue ZUGFeRD-Preview');
            const result = await generateZugferdPreview(invoiceData, token);
            pdfBlob = result.pdfBlob;
            draftId = result.draftId;
            setCurrentDraftId(draftId); // Neue Draft-ID für zukünftige Updates merken
            setSelectedDraftId(draftId); // Auch als ausgewählten Entwurf setzen für UI-Anzeige
            console.log('ZUGFeRD-Preview erfolgreich generiert:', draftId);

            // PDF herunterladen mit professionellem Dateinamen
            const url = window.URL.createObjectURL(pdfBlob);
            const customerName = (buyer.companyName || buyer.personName || buyer.name || 'Kunde')
                .replace(/[/\\?%*:|"<>äöüÄÖÜß]/g, (char) => {
                    const umlautMap: Record<string, string> = { 'ä': 'ae', 'ö': 'oe', 'ü': 'ue', 'Ä': 'Ae', 'Ö': 'Oe', 'Ü': 'Ue', 'ß': 'ss' };
                    return umlautMap[char] || '_';
                })
                .replace(/\s+/g, '_')
                .substring(0, 50);
            const link = document.createElement('a');
            link.href = url;
            link.download = `${customerName}-Rechnung-Vorschau-${new Date().toISOString().split('T')[0]}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            // Draft-Liste neu laden
            const drafts = await listInvoiceDrafts(token);
            const zugferdDrafts = drafts.filter(draft => draft.type === 'zugferd');
            setAvailableDrafts(zugferdDrafts);
        } catch (error: any) {
            console.error('Fehler beim Generieren der ZUGFeRD-Vorschau:', error);
            setDraftListError(mapErrorToUserMessage(error, t, { fallbackKey: 'errors.invoice.previewFailed' }));
        } finally {
            setIsGeneratingPreview(false);
        }
    };

    // NEU: Funktion zum Öffnen und Scrollen zum Abschlagsbereich
    const handleOpenDownPaymentSection = () => {
        // Abschlagsbereich öffnen falls er geschlossen ist
        if (!showDownPaymentSection) {
            setShowDownPaymentSection(true);
        }
        
        // Nach kurzer Verzögerung zum Element scrollen (damit es Zeit hat sich zu öffnen)
        setTimeout(() => {
            const downPaymentElement = document.getElementById('down-payment-section');
            if (downPaymentElement) {
                downPaymentElement.scrollIntoView({ 
                    behavior: 'smooth', 
                    block: 'start' 
                });
            }
        }, 100);
    };

    const handleSelectDraft = async (selectedOption: SingleValue<any>) => {
        if (!selectedOption || !token) {
            setSelectedDraftId(null);
            setCurrentDraftId(null); // Reset auch die aktuelle Draft-ID
            return;
        }

        setSelectedDraftId(selectedOption.value);
        setCurrentDraftId(selectedOption.value); // Setze die ausgewählte Draft-ID als aktuelle für Updates
        setIsLoadingDraftDetails(true);

        try {
            const draftDetails = await getInvoiceDraftDetails(selectedOption.value, token);
            console.log('Geladene ZUGFeRD-Draft-Details:', draftDetails);

            // Formular mit Draft-Daten befüllen
            const rawData = draftDetails.rawData;
            if (rawData) {
                // Setze alle Formularfelder mit den gespeicherten Daten
                setInvoiceDate(rawData.invoiceDate || new Date().toISOString().split('T')[0]);
                setCurrency(rawData.currency || 'EUR');
                setBuyer(rawData.buyer || { name: '', street: '', zip: '', city: '', country: 'DE' });
                setItems(rawData.items || []);
                setDeliveryDate(rawData.deliveryDate || '');
                setUseDifferentShipTo(rawData.useDifferentShipTo || false);
                setShipToAddress(rawData.shipToAddress || { name: '', street: '', zip: '', city: '', country: 'DE' });
                setPaymentTerms(rawData.paymentTerms || '');
                setNotes(rawData.notes || '');
                setBillingStartDate(rawData.billingStartDate || '');
                setBillingEndDate(rawData.billingEndDate || '');
                setTenderOrLotReference(rawData.tenderOrLotReference || '');
                setInvoicedObjectId(rawData.invoicedObjectId || '');
                setInvoicedObjectScheme(rawData.invoicedObjectScheme || '');
                setContractReference(rawData.contractReference || '');
                setProjectReferenceId(rawData.projectReferenceId || '');
                setProjectReferenceName(rawData.projectReferenceName || '');
                setPaymentDueDate(rawData.paymentDueDate || '');
                setPaymentMandateReference(rawData.paymentMandateReference || '');
                setBuyerOrderReference(rawData.buyerOrderReference || '');
                setSellerOrderReference(rawData.sellerOrderReference || '');
                
                // ⭐ NEU: Dokumentsprache wiederherstellen (falls gespeichert)
                if (rawData.documentLanguage) {
                    setDocumentLanguage(rawData.documentLanguage);
                }
                
                // NEU: Angebotsinformationen wiederherstellen
                if (draftDetails.relatedOfferId) {
                    setSelectedOfferId(draftDetails.relatedOfferId);
                    console.log('Wiederhergestellte relatedOfferId:', draftDetails.relatedOfferId);
                }
                if (draftDetails.workflowReferenceId) {
                    setOfferWorkflowReferenceId(draftDetails.workflowReferenceId);
                    console.log('Wiederhergestellte workflowReferenceId:', draftDetails.workflowReferenceId);
                }
                // Versuche Angebotsnummer zu laden falls verfügbar
                if (rawData.selectedOfferId || draftDetails.relatedOfferId) {
                    const offerId = rawData.selectedOfferId || draftDetails.relatedOfferId;
                    // Hier könnte man optional die Angebotsnummer laden
                    console.log('Draft basiert auf Angebot:', offerId);
                }
            }
        } catch (error: any) {
            console.error('Fehler beim Laden der ZUGFeRD-Draft-Details:', error);
            setDraftListError(mapErrorToUserMessage(error, t, { fallbackKey: 'errors.draft.loadFailed' }));
            setSelectedDraftId(null);
        } finally {
            setIsLoadingDraftDetails(false);
        }
    };

    const handleDeleteDraft = async (draftId: string) => {
        if (!token) {
            console.error('Kein Auth-Token verfügbar für Draft-Löschung');
            return;
        }

        // Optimistic Update: Sofort aus der Liste entfernen
        const originalDrafts = [...availableDrafts];
        const filteredDrafts = availableDrafts.filter(draft => draft.id !== draftId);
        setAvailableDrafts(filteredDrafts);

        // Wenn der gelöschte Draft der aktuell ausgewählte war, Auswahl zurücksetzen
        if (selectedDraftId === draftId) {
            setSelectedDraftId(null);
        }

        try {
            // Backend-Löschung im Hintergrund
            await deleteInvoiceDraft(draftId, token);
            console.log('ZUGFeRD-Draft erfolgreich gelöscht:', draftId);
        } catch (error: any) {
            console.error('Fehler beim Löschen des ZUGFeRD-Drafts:', error);
            
            // Bei Fehler: Original-Liste wiederherstellen
            setAvailableDrafts(originalDrafts);
            if (selectedDraftId === draftId) {
                setSelectedDraftId(draftId); // Auswahl wiederherstellen
            }
            
            setDraftListError(mapErrorToUserMessage(error, t, { fallbackKey: 'errors.draft.deleteFailed' }));
        }
    };

    // Down Payment Modal Funktionen
    const handleDownPaymentClick = () => {
        // Berechne den Gesamtbetrag korrekt mit Steuern
        const totalAmount = items.reduce((sum, item) => {
            const netPrice = item.netPrice * item.quantity;
            const taxAmount = netPrice * (item.taxRate / 100);
            return sum + netPrice + taxAmount;
        }, 0);

        setExistingDownPayments([]); // TODO: Lade existierende Abschlagsrechnungen
        setShowDownPaymentModal(true);
    };

    const closeDownPaymentModal = () => {
        setShowDownPaymentModal(false);
        // Don't reset existing down payments when closing modal
        // since they are now managed by the form hook
    };

    const handleDownPaymentCreated = async (newDownPayment: DownPayment) => {
        try {
            // Füge die neue Abschlagsrechnung zu den existierenden hinzu
            const updatedDownPayments = [...existingDownPayments, newDownPayment];
            const newTotalDownPaymentAmount = updatedDownPayments.reduce((sum, dp) => sum + dp.amount, 0);
            
            // Update all related states
            setExistingDownPayments(updatedDownPayments);
            setHasDownPayments(true);
            setTotalDownPaymentAmount(newTotalDownPaymentAmount);
            
            // Calculate remaining amount based on current items
            const currentTotal = items.reduce((sum, item) => {
                const netPrice = item.netPrice * item.quantity;
                const taxAmount = netPrice * (item.taxRate / 100);
                return sum + netPrice + taxAmount;
            }, 0);
            setRemainingAmount(Math.max(0, currentTotal - newTotalDownPaymentAmount));
            
            // Schließe das Modal
            closeDownPaymentModal();
            
            // Zeige Erfolgs-Nachricht (optional)
            logger.info('Abschlagsrechnung erfolgreich erstellt:', newDownPayment);
            
        } catch (error) {
            logger.error('Fehler beim Verarbeiten der erstellten Abschlagsrechnung:', error);
        }
    };

    // **FIX 32: Koordinierte Hook-Validierung**
    const validateAndHandleUpdates = useCallback(async (): Promise<boolean> => {
        try {
            // **DEBUG: Zeige an, was passiert**
            nativeConsole.log('=== validateAndHandleUpdates START ===');
            nativeConsole.log('buyer:', buyer);
            nativeConsole.log('items:', items);
            nativeConsole.log('checkCustomerUpdate function:', checkCustomerUpdate);
            
            // Sequenzielle Validierung - nur ein Dialog gleichzeitig
            nativeConsole.log('Calling checkCustomerUpdate...');
            const customerResult = await checkCustomerUpdate(buyer);
            nativeConsole.log('checkCustomerUpdate result:', customerResult);
            
            if (customerResult === false) {
                nativeConsole.log('Customer validation failed - stopping process');
                return false; // Validierung fehlgeschlagen - stoppe Prozess
            }

            nativeConsole.log('Calling checkAndHandleProductUpdate...');
            const productResult = await checkAndHandleProductUpdate(items);
            nativeConsole.log('checkAndHandleProductUpdate result:', productResult);
            
            if (productResult === false) {
                nativeConsole.log('Product validation failed - stopping process');
                return false; // Validierung fehlgeschlagen - stoppe Prozess
            }

            nativeConsole.log('=== validateAndHandleUpdates SUCCESS ===');
            return true; // Beide Validierungen erfolgreich
        } catch (error) {
            nativeConsole.error('Fehler bei der Validierung:', error);
            logger.error('Fehler bei der Validierung:', error);
            return false;
        }
    }, [buyer, items, checkCustomerUpdate, checkAndHandleProductUpdate]);

    // Handler für Abschlussrechnung
    const handleFinalInvoiceSubmit = useCallback(async (e: React.FormEvent) => {
        e.preventDefault();
        
        if (!currentUser || !token) {
            setSubmissionError('Authentifizierung erforderlich');
            return;
        }

        try {
            // **FIX 33: Koordinierte Validierung verwenden**
            const validationSuccessful = await validateAndHandleUpdates();
            if (!validationSuccessful) {
                return; // Dialog ist offen oder Fehler aufgetreten
            }

            // Importiere die Final Invoice API und Tax Utils
            const { createFinalInvoiceFromDownPayments } = await import('../api/downPaymentApi');
            const { calculateFinalInvoiceWithTaxes } = await import('../utils/taxCalculationUtils');
            
            // Berechne korrekte Steuerverteilung
            const taxCalculation = calculateFinalInvoiceWithTaxes(items, existingDownPayments);
            
            // Erstelle Final Invoice Request
            const finalInvoiceRequest = {
                baseDocumentId: selectedOfferId || '',
                baseDocumentType: 'offer' as const,
                baseDocumentNumber: selectedOfferNumber || undefined, // Benutzerfreundliche Angebotsnummer
                invoiceDate: invoiceDate,
                notes: notes || undefined,
                includeDownPaymentDetails: true,
                format: 'zugferd' as const,
                workflowReferenceId: offerWorkflowReferenceId || undefined,
                remainingAmount: remainingAmount,
                existingDownPayments: existingDownPayments,
                // Vollständige Dokumentdaten
                seller: sellerData || undefined,
                buyer: buyer,
                items: items,
                deliveryDate: deliveryDate || undefined,
                paymentTerms: paymentTerms || undefined,
                billingStartDate: billingStartDate || undefined,
                billingEndDate: billingEndDate || undefined,
                contractReference: contractReference || undefined,
                buyerOrderReference: buyerOrderReference || undefined,
                sellerOrderReference: sellerOrderReference || undefined,
                relatedOfferId: selectedOfferId || undefined,
                deliveryAddress: useDifferentShipTo ? shipToAddress : undefined
            };

            // Erstelle die Abschlussrechnung
            const result = await createFinalInvoiceFromDownPayments(finalInvoiceRequest, token);
            
            // Download der Datei
            const { downloadBlob } = await import('../api/invoiceApi');
            downloadBlob(result.blob, result.filename);
            
            // Formular zurücksetzen
            resetAllForms();
            
            // **FIX: Release customer meta lock after successful final invoice submission**
            releaseCustomerMetaLock();
            
            // Erfolgsmeldung entfernt

        } catch (error) {
            logger.error('Fehler bei der Abschlussrechnungserstellung:', error);
            setSubmissionError((error as Error).message || 'Unbekannter Fehler bei der Abschlussrechnungserstellung');
        }
    }, [
        currentUser, token, validateAndHandleUpdates, selectedOfferId, invoiceDate, notes, 
        offerWorkflowReferenceId, remainingAmount, existingDownPayments, sellerData, 
        deliveryDate, paymentTerms, billingStartDate, billingEndDate, contractReference, 
        buyerOrderReference, sellerOrderReference, useDifferentShipTo, shipToAddress, 
        resetAllForms, setSubmissionError, selectedOfferNumber
    ]);

    // Combine all potential errors for display
    const showUsageLimitPromo = submissionErrorType === 'usageLimit';
    const displayError = showUsageLimitPromo ? offerSelectionError : (submissionError || offerSelectionError);

    // M-UX-G4: Value-Framing für den 403-Limit-Moment (fetch-on-demand, s. Hook-JSDoc).
    const { revenueCents: usageLimitRevenueCents, resolved: usageLimitRevenueResolved } = useUsageLimitValueFraming(
        currentUser?.uid,
        showUsageLimitPromo,
    );

    // Auto-scroll zum Fehler wenn einer auftritt
    useEffect(() => {
        if (displayError && errorRef.current) {
            errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    }, [displayError]);

    // M-N3 (architecture-nudge.md §7.4): view_promotion GENAU 1x pro Banner-
    // Erscheinen (steigende Flanke von showUsageLimitPromo), unabhängig davon,
    // wie oft der Effekt wegen der async Umsatz-Auflösung erneut läuft — dafür
    // ein Ref-Latch statt eines reinen Dep-Arrays. Wir warten bewusst auf
    // `usageLimitRevenueResolved`, bevor wir feuern, statt sofort mit einem
    // Zwischenstand zu feuern: sonst würde für umsatztragende Nutzer zuerst
    // 'generic' und nach Auflösung des Fetches erneut 'value_framed' feuern
    // (Doppel-Fire, F-1 Review-Finding) — creative_slot kodiert, ob der
    // Umsatz-Value-Framing-Text (usageLimitPromo.valueMessage) oder die
    // generische Botschaft gezeigt wurde; PromotionEventData (M-N1) hat kein
    // eigenes value_framed-Boolean-Feld, daher hier über creative_slot
    // transportiert.
    const usageLimitBannerFiredRef = useRef(false);
    useEffect(() => {
        if (!showUsageLimitPromo) {
            usageLimitBannerFiredRef.current = false;
            return;
        }
        if (usageLimitBannerFiredRef.current || !usageLimitRevenueResolved) return;
        usageLimitBannerFiredRef.current = true;
        analyticsService.trackViewPromotion({
            promotion_id: 'usage_limit_banner',
            creative_slot: usageLimitRevenueCents !== null ? 'value_framed' : 'generic',
            limit_value: FREEMIUM_INVOICE_LIMIT,
        });
    }, [showUsageLimitPromo, usageLimitRevenueResolved, usageLimitRevenueCents]);

    // Render mobile version if on mobile device
    if (isMobile) {
        return <MobileInvoiceDashboard />;
    }

    // Render desktop version
    return (
        <div className="container mx-auto p-4 bg-surface min-h-screen text-primary">
            <h1 className="text-3xl font-bold mb-6 text-accent-ink">{t('newInvoice')}</h1>

            <InvoiceTypeSwitch currentType="zugferd" basePath="/dashboard" zugferdPath={INVOICE_FORM_ROUTE} />

            {/* ⭐ Storno-Banner: Zeigt an, dass eine Stornorechnung erstellt wird */}
            {cancellationForInvoiceId && cancellationInvoiceNumber && (
                <div className="p-4 mb-6 bg-[var(--tone-warning-bg)] border-2 border-amber-500 rounded-lg">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center">
                            <svg className="w-6 h-6 text-[var(--tone-warning-fg)] mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                            <div>
                                <h3 className="text-lg font-semibold text-[var(--tone-warning-fg)]">
                                    {t('cancellation.status.isCancellation', 'Stornorechnung')}
                                </h3>
                                <p className="text-sm text-[var(--tone-warning-fg)] mt-0.5">
                                    {t('cancellation.creatingFor', 'Sie erstellen eine Stornorechnung für Rechnung')}{' '}
                                    <span className="font-bold text-[var(--tone-warning-fg)]">{cancellationInvoiceNumber}</span>
                                </p>
                            </div>
                        </div>
                        <button
                            type="button"
                            onClick={() => {
                                setCancellationForInvoiceId(null);
                                setCancellationInvoiceNumber(null);
                                resetInvoiceForm();
                                // URL-Parameter entfernen
                                setSearchParams({});
                            }}
                            className="px-3 py-1.5 text-sm bg-[var(--tone-warning-bg)] hover:brightness-110 text-[var(--tone-warning-fg)] rounded-md transition-colors flex items-center"
                        >
                            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                            {t('common:cancel', 'Abbrechen')}
                        </button>
                    </div>
                </div>
            )}

            {/* Info-Box über ZUGFeRD */}
            <div className="p-4 mb-6 bg-[var(--tone-info-bg)] border border-blue-500 rounded-lg">
                <div className="flex items-start">
                    <svg className="w-5 h-5 text-[var(--tone-info-fg)] mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                    <div>
                        <h3 className="text-sm font-medium text-[var(--tone-info-fg)]">{t('invoiceFormat.title')}</h3>
                        <p className="text-sm text-[var(--tone-info-fg)] mt-1">
                            {t('invoiceFormat.description')}
                        </p>
                    </div>
                </div>
            </div>

            {/* M-UX-G4: Free-Tier-Verbrauchszähler — VOR dem Limit-Moment sichtbar, kein Zähler für Premium.
                Gate auf !sellerDataLoading: verhindert einen "0 von 4"-Flash für Premium-Nutzer, solange
                isPremium noch seinen Default (false) trägt, während sellerData lädt. */}
            {!sellerDataLoading && (
                <InvoiceUsageMeter count={sellerData?.monthlyInvoiceCount ?? 0} isPremium={isPremium} />
            )}

            {/* Draft Management Section */}
            {hasRequiredSellerData && (
                <InvoiceDraftSelector
                    drafts={availableDrafts}
                    selectedDraftId={selectedDraftId}
                    onSelectDraft={handleSelectDraft}
                    onDeleteDraft={handleDeleteDraft}
                    isLoading={isLoadingDrafts}
                    error={draftListError}
                    filterType="zugferd"
                />
            )}

            {/* Seller Data Check */}
            {!sellerDataLoading && !hasRequiredSellerData && (
                <div className="p-6 mb-6 bg-[var(--tone-accent-bg)] border border-indigo-500 rounded-lg">
                    <div className="flex flex-col items-center text-center">
                        <h3 className="text-xl font-semibold text-[var(--tone-accent-fg)] mb-3">{t('common:sellerDataIncomplete.title')}</h3>
                        <p className="text-secondary mb-4">
                            {!hasSellerTaxIdentification(sellerData) 
                                ? t('common:sellerDataIncomplete.taxIdMissing')
                                : t('common:sellerDataIncomplete.message')}
                        </p>
                        <Link 
                            to="/dashboard/konto" 
                            className="px-5 py-3 text-[#0C1322] bg-indigo-600 hover:bg-indigo-700 rounded-md font-medium"
                        >
                            {t('common:sellerDataIncomplete.completeData')}
                        </Link>
                    </div>
                </div>
            )}

            {showUsageLimitPromo && (
                <div className="mb-4 p-5 bg-[var(--tone-accent-bg)] border border-indigo-600 rounded-lg" role="status">
                    <div className="flex items-start">
                        <svg className="w-6 h-6 text-[var(--tone-accent-fg)] mt-0.5 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M12 3a9 9 0 100 18 9 9 0 000-18z" />
                        </svg>
                        <div className="flex-1">
                            <h3 className="text-lg font-semibold text-[var(--tone-accent-fg)]">
                                {t('common:usageLimitPromo.title', { limit: FREEMIUM_INVOICE_LIMIT })}
                            </h3>
                            <p className="text-sm text-[var(--tone-accent-fg)] mt-1">
                                {usageLimitRevenueCents !== null
                                    ? t('common:usageLimitPromo.valueMessage', {
                                          amount: formatCurrency(centsToEuros(usageLimitRevenueCents)),
                                      })
                                    : t('common:usageLimitPromo.message')}
                            </p>
                            <p className="text-xs text-[var(--tone-accent-fg)] mt-1">
                                {t('common:usageLimitPromo.yearlyAnchor')}
                            </p>
                            <div className="mt-3 flex flex-wrap items-center gap-3">
                                <Link
                                    to={buildUpgradeHref('usage_limit_banner')}
                                    onClick={() => analyticsService.trackSelectPromotion({
                                        promotion_id: 'usage_limit_banner',
                                        creative_slot: usageLimitRevenueCents !== null ? 'value_framed' : 'generic',
                                        limit_value: FREEMIUM_INVOICE_LIMIT,
                                    })}
                                    className="inline-flex items-center px-4 py-2 rounded-md bg-indigo-500 hover:bg-indigo-400 text-[#0C1322] text-sm font-semibold transition-colors"
                                >
                                    {t('common:usageLimitPromo.cta')}
                                </Link>
                                <span className="text-xs text-[var(--tone-accent-fg)]">
                                    {t('common:usageLimitPromo.note')}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Error Display */}
            {displayError && (
                <div 
                    ref={errorRef}
                    className="mb-4 p-4 bg-[var(--tone-danger-bg)] border border-[var(--tone-danger-border)] text-[var(--tone-danger-fg)] rounded"
                    role="alert"
                >
                    <strong className="font-bold">{t('form.error')}:</strong>
                    <span className="block sm:inline"> {displayError}</span>
                </div>
            )}

            {/* Main Form */}
            {hasRequiredSellerData && (
                <div className="grid grid-cols-1 gap-6">
                    <div className="bg-surface-raised shadow-lg rounded-lg p-6 border border-hairline space-y-6">
                        <form onSubmit={handleFormSubmit}>

                            {/* Offer Selector */}
                            <div className="mb-6 p-4 border border-hairline rounded-md shadow-sm bg-surface-inset">
                                <h2 className="text-lg font-medium mb-4 text-secondary">{t('form.useOfferAsBasis')}</h2>
                                <RelatedOfferSelector 
                                    onSelectOffer={handleOfferSelection} 
                                    selectedOfferId={selectedOfferId} 
                                />
                                {selectedOfferId && (
                                    <div className="mt-2 text-sm text-muted">
                                        {t('offer.invoiceBasedOn')}: <span className="font-medium text-primary">{selectedOfferNumber}</span>
                                    </div>
                                )}
                            </div>

                            {/* Down Payment Section - Show when offer with workflow is selected */}
                            {offerWorkflowReferenceId && (
                                <div id="down-payment-section" className="p-6 bg-surface-raised rounded-lg border border-hairline shadow-inner mb-6">
                                    <div className="flex items-center justify-between mb-4 border-b border-strong pb-2">
                                        <h2 className="text-xl font-semibold text-primary">Abschlagsrechnungen</h2>
                                        <button
                                            type="button"
                                            onClick={() => setShowDownPaymentSection(!showDownPaymentSection)}
                                            className="text-accent-ink hover:text-accent-ink text-sm font-medium"
                                        >
                                            {showDownPaymentSection ? 'Ausblenden' : 'Anzeigen'}
                                        </button>
                                    </div>
                                    
                                    {isLoadingDownPayments ? (
                                        <div className="text-center py-4">
                                            <div className="inline-flex items-center">
                                                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-muted" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                </svg>
                                                <span className="text-secondary">Lade Abschlagsrechnungen...</span>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="space-y-3">
                                            <div className="bg-surface-inset p-4 rounded-md">
                                                <div className="grid grid-cols-3 gap-4 text-sm">
                                                    <div>
                                                        <span className="text-muted">Bereits bezahlt:</span>
                                                        <div className="text-[var(--tone-success-fg)] font-semibold">
                                                            {new Intl.NumberFormat('de-DE', { style: 'currency', currency: currency }).format(totalDownPaymentAmount)}
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <span className="text-muted">Noch offen:</span>
                                                        <div className="text-[var(--tone-warning-fg)] font-semibold">
                                                            {new Intl.NumberFormat('de-DE', { style: 'currency', currency: currency }).format(remainingAmount)}
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <span className="text-muted">Anzahl Abschläge:</span>
                                                        <div className="text-[var(--tone-info-fg)] font-semibold">
                                                            {existingDownPayments.length}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            
                                            {showDownPaymentSection && selectedOfferId && (
                                                <div className="space-y-2">
                                                    {existingDownPayments.length > 0 && (
                                                        <>
                                                            <h4 className="text-sm font-medium text-secondary mb-2">Vorhandene Abschlagsrechnungen:</h4>
                                                            {existingDownPayments.map((dp, index) => (
                                                                <div key={dp.id} className="bg-surface-hover p-3 rounded text-sm">
                                                                    <div className="flex justify-between items-center">
                                                                        <span className="text-secondary">
                                                                            {dp.invoiceNumber} ({formatDateCentral(dp.invoiceDate, { locale: i18n.language })})
                                                                        </span>
                                                                        <span className="text-[var(--tone-success-fg)] font-medium">
                                                                            {new Intl.NumberFormat('de-DE', { style: 'currency', currency: dp.currency }).format(dp.amount)}
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </>
                                                    )}
                                                    
                                                    {/* Inline-Erstellungsbereich für neue Abschlagsrechnung */}
                                                    <div className="bg-surface-inset p-4 rounded border border-strong">
                                                        <h5 className="text-sm font-medium text-secondary mb-3">Neue Abschlagsrechnung erstellen</h5>
                                                        <div className="space-y-3">
                                                            <div className="flex space-x-3">
                                                                <div className="flex-1">
                                                                    <label className="block text-xs text-muted mb-1">{t('downPayment.typeLabel')}</label>
                                                                    <select
                                                                        className="w-full px-3 py-2 bg-surface-hover border border-gray-500 rounded text-primary text-sm"
                                                                        value={inlineDownPaymentType}
                                                                        onChange={(e) => setInlineDownPaymentType(e.target.value as 'percentage' | 'amount')}
                                                                    >
                                                                        <option value="percentage">Prozentsatz</option>
                                                                        <option value="amount">Betrag</option>
                                                                    </select>
                                                                </div>
                                                                <div className="flex-1">
                                                                    <label className="block text-xs text-muted mb-1">
                                                                        Wert {inlineDownPaymentType === 'percentage' ? '(%)' : '(€)'}
                                                                    </label>
                                                                    <input 
                                                                        type="number" 
                                                                        className="w-full px-3 py-2 bg-surface-hover border border-gray-500 rounded text-primary text-sm"
                                                                        placeholder={inlineDownPaymentType === 'percentage' ? '50' : '1000'}
                                                                        value={inlineDownPaymentValue}
                                                                        onChange={(e) => setInlineDownPaymentValue(e.target.value)}
                                                                        min="0"
                                                                        step={inlineDownPaymentType === 'percentage' ? '0.01' : '0.01'}
                                                                    />
                                                                </div>
                                                            </div>
                                                            <div>
                                                                <label className="block text-xs text-muted mb-1">Rechnungsdatum</label>
                                                                <input 
                                                                    type="date" 
                                                                    className="w-full px-3 py-2 bg-surface-hover border border-gray-500 rounded text-primary text-sm"
                                                                    value={inlineDownPaymentDate}
                                                                    onChange={(e) => setInlineDownPaymentDate(e.target.value)}
                                                                />
                                                            </div>
                                                            <div>
                                                                <label className="block text-xs text-muted mb-1">{t('downPayment.notesLabel')}</label>
                                                                <textarea
                                                                    className="w-full px-3 py-2 bg-surface-hover border border-gray-500 rounded text-primary text-sm"
                                                                    rows={2}
                                                                    placeholder={t('downPayment.notesPlaceholder')}
                                                                    value={inlineDownPaymentNotes}
                                                                    onChange={(e) => setInlineDownPaymentNotes(e.target.value)}
                                                                />
                                                            </div>
                                                            <Button
                                                                type="button"
                                                                variant="primary"
                                                                className="w-full"
                                                                loading={isCreatingInlineDownPayment}
                                                                disabled={isCreatingInlineDownPayment || !inlineDownPaymentValue || parseFloat(inlineDownPaymentValue) <= 0}
                                                                onClick={async () => {
                                                                    const result = await handleInlineDownPaymentCreation('zugferd');
                                                                    if (!result.success) {
                                                                        alert(`Fehler: ${result.error}`);
                                                                    }
                                                                }}
                                                            >
                                                                {isCreatingInlineDownPayment ? 'Erstelle...' : 'Abschlagsrechnung erstellen'}
                                                            </Button>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Form Sections */}
                            <div className="p-6 bg-surface-raised rounded-lg border border-hairline shadow-inner mb-6">
                                <h2 className="text-xl font-semibold mb-4 border-b border-strong pb-2 text-primary">{t('sections.invoiceDetails')}</h2>
                                <InvoiceMetaForm 
                                    invoiceDate={invoiceDate}
                                    setInvoiceDate={setInvoiceDate}
                                    currency={currency}
                                    setCurrency={setCurrency}
                                    deliveryDate={deliveryDate}
                                    setDeliveryDate={setDeliveryDate}
                                    invoiceNumberPreview={zugferdPreview?.preview}
                                    isLoadingPreview={isLoadingPreview}
                                    showTitle={false}
                                />
                                
                                {/* NEU: Dokumentsprache-Auswahl */}
                                <div className="mt-6 pt-6 border-t border-hairline">
                                    <DocumentLanguageSelector
                                        value={documentLanguage}
                                        onChange={setDocumentLanguage}
                                        disabled={isSubmitting}
                                    />
                                </div>
                            </div>
                            
                            <div className="p-6 bg-surface-raised rounded-lg border border-hairline shadow-inner mb-6">
                                <h2 className="text-xl font-semibold mb-4 border-b border-strong pb-2 text-primary">{t('sections.buyerInformation')}</h2>
                                <BuyerDetailsForm 
                                    buyer={buyer}
                                    setBuyer={setBuyer}
                                    onCustomerSelect={handleCustomerSelection}
                                    showTitle={false}
                                />
                            </div>
                            
                            <div className="p-6 bg-surface-raised rounded-lg border border-hairline shadow-inner mb-6">
                                <h2 className="text-xl font-semibold mb-4 border-b border-strong pb-2 text-primary">{t('sections.invoicePositions')}</h2>
                                <InvoiceItemsForm
                                    items={items}
                                    setItems={setItems}
                                    currency={currency}
                                    showTitle={false}
                                    onProductSelect={handleProductSelectionForHook}
                                    defaultTaxCategorySelection={defaultTaxCategorySelection}
                                    buyerVatId={buyer.vatNumber}
                                />
                            </div>

                            {/* Optional Fields Section */}
                            <CollapsibleSection
                                title={t('sections.advancedOptions')}
                                defaultExpanded={isAdvancedMode}
                                error={
                                    billingPeriodError
                                        ? (billingPeriodError === 'incomplete'
                                            ? t('advanced.periodIncompleteError')
                                            : t('advanced.periodOrderError'))
                                        : undefined
                                }
                            >
                                <OptionalFieldsForm
                                    paymentTerms={paymentTerms}
                                    setPaymentTerms={setPaymentTerms}
                                    notes={notes}
                                    setNotes={setNotes}
                                    billingStartDate={billingStartDate}
                                    setBillingStartDate={setBillingStartDate}
                                    billingEndDate={billingEndDate}
                                    setBillingEndDate={setBillingEndDate}
                                    tenderOrLotReference={tenderOrLotReference}
                                    setTenderOrLotReference={setTenderOrLotReference}
                                    invoicedObjectId={invoicedObjectId}
                                    setInvoicedObjectId={setInvoicedObjectId}
                                    invoicedObjectScheme={invoicedObjectScheme}
                                    setInvoicedObjectScheme={setInvoicedObjectScheme}
                                    contractReference={contractReference}
                                    setContractReference={setContractReference}
                                    projectReferenceId={projectReferenceId}
                                    setProjectReferenceId={setProjectReferenceId}
                                    projectReferenceName={projectReferenceName}
                                    setProjectReferenceName={setProjectReferenceName}
                                    paymentDueDate={paymentDueDate}
                                    setPaymentDueDate={setPaymentDueDate}
                                    paymentMandateReference={paymentMandateReference}
                                    setPaymentMandateReference={setPaymentMandateReference}
                                    buyerOrderReference={buyerOrderReference}
                                    setBuyerOrderReference={setBuyerOrderReference}
                                    sellerOrderReference={sellerOrderReference}
                                    setSellerOrderReference={setSellerOrderReference}
                                    defaultTaxCategorySelection={defaultTaxCategorySelection}
                                    setDefaultTaxCategorySelection={setDefaultTaxCategorySelection}
                                />
                            </CollapsibleSection>

                            {/* Per Email versenden */}
                            <div className="mt-4 p-5 bg-surface-raised rounded-lg border border-hairline shadow-inner">
                                <div className="flex items-center gap-2 mb-3">
                                    <input
                                        type="checkbox"
                                        id="sendToEmailToggle"
                                        checked={!!sendToEmail}
                                        onChange={(e) => {
                                            if (e.target.checked) {
                                                setSendToEmail(buyer?.email || '');
                                            } else {
                                                setSendToEmail(null);
                                            }
                                        }}
                                        className="w-4 h-4 accent-indigo-500"
                                    />
                                    <label htmlFor="sendToEmailToggle" className="text-sm font-medium text-secondary">
                                        {t('emailSend.invoiceToggleLabel', { ns: 'common' })}
                                    </label>
                                </div>
                                {sendToEmail !== null && (
                                    <div>
                                        <input
                                            type="email"
                                            id="sendToEmailInput"
                                            value={sendToEmail}
                                            onChange={(e) => setSendToEmail(e.target.value)}
                                            className="input-field placeholder-subtle"
                                            placeholder={t('emailSend.emailPlaceholder', { ns: 'common' })}
                                            required
                                        />
                                        <p className="text-xs text-muted mt-1">{t('emailSend.hint', { ns: 'common' })}</p>
                                    </div>
                                )}
                            </div>

                            {/* Action Buttons */}
                            <div className="mt-8 flex justify-end space-x-4">
                                {/* Draft Save Button */}
                                <button
                                    type="button"
                                    onClick={handleSaveDraft}
                                    disabled={isSavingDraft || !token}
                                    className="inline-flex justify-center py-2 px-6 border border-transparent shadow-sm text-md font-medium rounded-md text-primary bg-surface-hover hover:bg-surface-inset focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {isSavingDraft ? (
                                        <>
                                            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-primary" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                            {t('loading', { ns: 'common' })}...
                                        </>
                                    ) : (
                                                                                                                          t('draft.saveAsDraft')
                                    )}
                                </button>

                                {/* Preview Generate Button */}
                                <button
                                    type="button"
                                    onClick={handleGeneratePreview}
                                    disabled={isGeneratingPreview || !token}
                                    className="inline-flex justify-center py-2 px-6 border border-transparent shadow-sm text-md font-medium rounded-md text-white bg-yellow-600 hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 transition disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {isGeneratingPreview ? (
                                        <>
                                            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-primary" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                            {t('loading', { ns: 'common' })}...
                                        </>
                                    ) : (
                                        t('actions.generatePreview')
                                    )}
                                </button>

                                {/* Abschlag Button - Nur anzeigen wenn Angebot mit Workflow ausgewählt ist */}
                                {offerWorkflowReferenceId && (
                                    <button
                                        type="button"
                                        onClick={handleOpenDownPaymentSection}
                                        className="inline-flex justify-center py-2 px-6 border border-transparent shadow-sm text-md font-medium rounded-md text-primary bg-surface-hover hover:bg-surface-inset focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition"
                                    >
                                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                        </svg>
                                        Abschlag
                                    </button>
                                )}

                                {/* Submit Button */}
                                <Button
                                    type="submit"
                                    variant="primary"
                                    className="px-6 py-2"
                                    loading={isSubmitting}
                                    disabled={isSubmitting}
                                >
                                    {isSubmitting
                                        ? `${t('loading', { ns: 'common' })}...`
                                        : hasDownPayments ? t('actions.createFinalInvoice') : t('actions.createInvoice')}
                                </Button>
                            </div>

                            {/* Erfolgs-Nachricht für Entwurf-Speicherung - in separater Zeile */}
                            {draftSaveSuccess && (
                                <div className="mt-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded-md flex items-center">
                                    <svg className="flex-shrink-0 mr-2 h-5 w-5 text-green-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                    </svg>
                                    {draftSaveSuccess}
                                </div>
                            )}
                        </form>
                    </div>
                </div>
            )}

            {/* Customer Dialog */}
            <ConfirmationDialog 
                isOpen={customerDialogOpen}
                isProcessing={isSubmitting}
                title={customerDialogConfig.title}
                message={customerDialogConfig.message}
                options={customerDialogConfig.options || []}
                onClose={() => setCustomerDialogOpen(false)}
                dataComparison={customerDialogConfig.action === 'update' ? {
                    original: customerDialogConfig.original || {},
                    modified: customerDialogConfig.data,
                    displayFields: [
                        { key: 'name', label: 'Name' },
                        { key: 'companyName', label: 'Firmenname' },
                        { key: 'personName', label: 'Ansprechpartner' },
                        { key: 'street', label: 'Straße' },
                        { key: 'zip', label: 'PLZ' },
                        { key: 'city', label: 'Ort' },
                        { key: 'country', label: 'Land' }
                    ]
                } : undefined}
            />

            {/* Product Dialog */}
            <ConfirmationDialog 
                isOpen={productDialogOpen}
                isProcessing={isSubmitting}
                title={productDialogConfig.title}
                message={productDialogConfig.message}
                options={[
                    { 
                        label: 'Änderungen speichern', 
                        action: () => onProductDialogAction('process'),
                        variant: 'primary' 
                    },
                    { 
                        label: 'Abbrechen', 
                        action: () => onProductDialogAction('cancel'),
                        variant: 'secondary'
                    }
                ]}
                onClose={() => setProductDialogOpen(false)}
                dataComparison={productDialogConfig.itemsToProcess.length === 1 && productDialogConfig.itemsToProcess[0].action === 'update' ? {
                    original: productDialogConfig.itemsToProcess[0].originalProduct || {},
                    modified: productDialogConfig.itemsToProcess[0].item,
                    displayFields: [
                        { key: 'name', label: 'Name' },
                        { key: 'description', label: 'Beschreibung' },
                        { key: 'netPrice', label: 'Netto-Preis' },
                        { key: 'taxRate', label: 'Steuersatz' },
                        { key: 'unitCode', label: 'Einheit' }
                    ]
                } : undefined}
            />

            {/* Down Payment Modal */}
            {showDownPaymentModal && (
                <DownPaymentModal
                    isOpen={showDownPaymentModal}
                    onClose={closeDownPaymentModal}
                    baseDocument={{
                        id: 'current-invoice-draft',
                        type: 'invoice' as const,
                        totalAmount: items.reduce((sum, item) => {
                            const netPrice = item.netPrice * item.quantity;
                            const taxAmount = netPrice * (item.taxRate / 100);
                            return sum + netPrice + taxAmount;
                        }, 0),
                        currency: currency,
                        documentNumber: zugferdPreview?.preview || 'Entwurf',
                        // Alle Formulardaten für die e-Rechnung-Generierung
                        seller: sellerData,
                        buyer: buyer,
                        items: items,
                        invoiceDate: invoiceDate,
                        deliveryDate: deliveryDate,
                        paymentTerms: paymentTerms,
                        notes: notes,
                        dueDate: paymentDueDate,
                        billingStartDate: billingStartDate,
                        billingEndDate: billingEndDate,
                        contractReference: contractReference,
                        buyerOrderReference: buyerOrderReference,
                        sellerOrderReference: sellerOrderReference,
                        relatedOfferId: selectedOfferId,
                        workflowReferenceId: offerWorkflowReferenceId,
                        deliveryAddress: useDifferentShipTo ? shipToAddress : null
                    }}
                    existingDownPayments={existingDownPayments}
                    onDownPaymentCreated={handleDownPaymentCreated}
                    format="zugferd"
                />
            )}

            <TranslationReviewModal
                isOpen={isTranslationModalOpen}
                isLoading={isTranslationLoading}
                originalLanguage={sellerData?.preferredLanguage || 'de'}
                targetLanguage={documentLanguage}
                fields={translationFields}
                onFieldChange={handleTranslationFieldChange}
                onConfirm={() => confirmTranslation(translationFields, documentLanguage)}
                onCancel={cancelTranslation}
            />
        </div>
    );
};

export default InvoiceDashboard; 